import React, {FC, useEffect, useRef, useState} from 'react';
import classNames from 'classnames';
import Picker from 'components/Bulk/Picker';
import Button from 'components/Button';
import {createPurchase, setupStore} from 'components/checkout/setup';
import {usePrices} from 'components/utils/usePrices';
import {Routes} from 'constants/index';
import LoadingState from 'pages/pricing-packages/components/Content/LoadingState';
import Pricing from 'pages/pricing-packages/components/Content/Pricing';
import {useContextConfig} from 'pages/pricing-packages/components/context';
import {useQueryValues} from 'utils/queryObj';

import * as styles from './styles.module.scss';

interface PackageSliderProps {
	children?: React.ReactNode;
	center?: boolean;
}

// Constants for configuration
const MIN_AMOUNT = 1;
const MAX_AMOUNT = 20;
const MAX_LOAD_ATTEMPTS = 3;

const PackageSlider: FC<PackageSliderProps> = ({children, center}) => {
	const {read, update} = useQueryValues('bulk');
	const config = useContextConfig();

	// Static price data loading
	const {isLoading: isStaticLoading, hasError: staticError} = usePrices();
	const loadAttempts = useRef(0);
	const [isStaticPriceLoaded, setIsStaticPriceLoaded] = useState(false);

	// Handle static price data loading with retries
	useEffect(() => {
		// If already loaded, nothing to do
		if (isStaticPriceLoaded) return;

		// If still loading, wait for it to complete
		if (isStaticLoading) return;

		// If we have no error, mark as loaded
		if (!staticError) {
			setIsStaticPriceLoaded(true);
			return;
		}

		// If there was an error, retry up to MAX_LOAD_ATTEMPTS times
		if (loadAttempts.current < MAX_LOAD_ATTEMPTS) {
			// Use exponential backoff for retries
			const delay = 150 * Math.pow(1.5, loadAttempts.current);
			const timeoutId = setTimeout(() => {
				loadAttempts.current += 1;
				// Force re-render by updating state
				setIsStaticPriceLoaded(false);
			}, delay);

			return () => clearTimeout(timeoutId);
		}
	}, [isStaticPriceLoaded, isStaticLoading, staticError]);

	// Initialize quantity from URL parameters or default
	const [qty, setQty] = useState<number>(() => {
		const cnt = Number(read({cnt: `${MIN_AMOUNT}`}).cnt);
		return cnt >= MIN_AMOUNT && cnt <= MAX_AMOUNT ? cnt : MIN_AMOUNT;
	});

	// Get bulk prices for the current quantity
	const prices = usePrices({name: 'DEXA', bulkQty: MAX_AMOUNT});

	// Check if the current price is available for the selected quantity
	const currentPriceReady = isStaticPriceLoaded && prices && prices.discPrices[qty - 1] !== undefined;

	// Calculate prices for current quantity
	const {costPer, costTotal} = currentPriceReady && prices
		? (() => {
			const total = prices.discPrices[qty - 1]?.total ?? 0;
			return {
				costPer: total / qty,
				costTotal: total,
			};
		})()
		: {costPer: 0, costTotal: 0};

	if (!isStaticPriceLoaded) {
		return (
			<LoadingState
				center={center}
				message={'Loading pricing information...'}
			/>
		);
	}

	const perProps = {
		center,
		qty: 1,
		savings: false,
		text: 'cost per scan',
		total: currentPriceReady ? costPer : 0,
	};

	const totalProps = {
		center,
		qty,
		text: `total for ${qty} scan${qty === 1 ? '' : 's'}`,
		total: currentPriceReady ? costTotal : 0,
	};

	const handleContinue = () => {
		setupStore(
			config.setup ? (
				config.setup({
					quantity: qty,
				})
			) : (
				createPurchase({
					product: prices ? prices.id : '',
					quantity: qty,
					redirect: Routes.dashboard,
				})
			)
		);
	};

	const handleQuantityChange = (v: number) => {
		update({cnt: `${v}`});
		setQty(v);
	};

	return (
		<div className={classNames({
			[styles.bulk]: true,
			[styles.center]: center,
		})}>
			{children}
			<p>Number of scan credits</p>
			<Picker
				label={false}
				defaultValue={qty}
				min={MIN_AMOUNT}
				max={MAX_AMOUNT}
				onChange={handleQuantityChange}
			/>
			{currentPriceReady ? (
				<>
					<Pricing {...perProps} />
					<hr />
					<Pricing {...totalProps} />
					<Button variant='primary' onClick={handleContinue}>
						Continue
					</Button>
				</>
			) : (
				<>
					<div className={styles.loading}>
						Loading price data for {qty} scan{qty === 1 ? '' : 's'}...
					</div>
					<Button variant='primary' onClick={handleContinue} disabled>
						Continue
					</Button>
				</>
			)}
		</div>
	);
};

export default PackageSlider;
